import type { Address } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { createDataReader } from '@nx/ui';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createContext } from 'react';
import type { PropsWithChildren } from 'react';

import { getFirstEnv } from '../../../get-aura-api-url';
import type { BillingFormData } from './billing-form-data';

const stripeReader = createDataReader(
  () => {
    const stripeKey = getFirstEnv(
      import.meta.env.R_VITE_STRIPE_PUBLISHABLE_KEY,
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
    );

    if (stripeKey === undefined) {
      throw new Error('Unable to load Stripe.js');
    }

    return loadStripe(stripeKey);
  },
  { defer: true },
);

export const InjectionErrorContext = createContext<string | null>(null);

/**
 * Wrapper class to pass Stripe context to child elements.
 */
export default function StripeProvider({ children }: PropsWithChildren) {
  if (window.location.protocol !== 'https:') {
    const injectionError = 'Unable to display payment form. You are running on an insecure connection';
    return <InjectionErrorContext.Provider value={injectionError}>{children}</InjectionErrorContext.Provider>;
  }

  return (
    <InjectionErrorContext.Provider value={null}>
      <Elements
        options={{
          fonts: [
            {
              cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans:400,700',
            },
          ],
        }}
        stripe={stripeReader.read()}
      >
        {children}
      </Elements>
    </InjectionErrorContext.Provider>
  );
}

// This is the expected format for the stripe card token
const transformToStripeCustomerTokenAddress = (address: Address) => ({
  address_line1: address.line1,
  address_country: address.country,
  address_city: address.city,
  address_zip: address.postalCode,
  address_state: address.state,
});

export const stripePayloadWithServiceAddress = (formData: BillingFormData, email: string) => {
  const customerAddress = formData.address;

  const serviceAddress = {
    name: formData.name,
    address: formData.serviceAddress ?? customerAddress,
  };

  const payload = {
    ...transformToStripeCustomerTokenAddress(formData.address),
    address: customerAddress,
    shipping: serviceAddress,
    name: isNotNullish(formData.companyName) ? formData.companyName : formData.name,
    email,
  };

  return payload;
};

export const stripePayloadWithoutServiceAddress = (formData: BillingFormData, email: string) => {
  const customerAddress = formData.address;

  let shipping = undefined;
  let { name } = formData;

  if (isNotNullish(formData.companyName)) {
    // Company name is set but no separate shipping address
    // In order for the company name to appear on invoices, we need to store it
    // as the name on the customer. The shipping address in this case becomes
    // the person tied to the credit card.

    name = formData.companyName;
    shipping = {
      name: formData.name,
      address: customerAddress,
    };
  }

  const payload = {
    ...transformToStripeCustomerTokenAddress(formData.address),
    address: customerAddress,
    shipping,
    name,
    email,
  };

  return payload;
};
